import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { formatCutoffTime } from "@/lib/availability";
import { CreateBookingInput } from "@/lib/booking.schema";

const SlotInput = z.object({
  date: z.string(),
  lessonTypeId: z.string().uuid(),
});

const BookableDatesInput = z.object({
  start: z.string(),
  end: z.string(),
  lessonTypeId: z.string().uuid(),
});

export const getLessonTypes = createServerFn({ method: "GET" }).handler(async () => {
  const { listLessonTypes } = await import("@/lib/booking.server");
  return listLessonTypes();
});

export const getBookingCutoff = createServerFn({ method: "GET" }).handler(async () => {
  const { loadBookingSettings } = await import("@/lib/booking.server");
  const schedule = await loadBookingSettings();
  return {
    cutoffTime: schedule.cutoffTime,
    cutoffLabel: formatCutoffTime(schedule.cutoffTime),
  };
});

/** Public availability — always enforces the booking cutoff. */
export const getAvailableSlots = createServerFn({ method: "POST" })
  .validator((d) => SlotInput.parse(d))
  .handler(async ({ data }) => {
    const { loadAvailabilitySnapshot } = await import("@/lib/availability-loader.server");
    const { slotsForDate } = await import("@/lib/availability-loader");
    const snapshot = await loadAvailabilitySnapshot(data.date, data.date, data.lessonTypeId);
    if (!snapshot) return [];
    return slotsForDate(snapshot, data.date, { skipCutoff: false });
  });

/** Public bookable dates — always enforces the booking cutoff. */
export const getBookableDates = createServerFn({ method: "POST" })
  .validator((d) => BookableDatesInput.parse(d))
  .handler(async ({ data }) => {
    const { loadAvailabilitySnapshot } = await import("@/lib/availability-loader.server");
    const { bookableDateKeys } = await import("@/lib/availability-loader");
    const snapshot = await loadAvailabilitySnapshot(data.start, data.end, data.lessonTypeId);
    if (!snapshot) return [];
    return bookableDateKeys(snapshot, data.start, data.end, { skipCutoff: false });
  });

export const getMyPackages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { listBookableMemberPackages } = await import("@/lib/member-package");
    return listBookableMemberPackages(context.supabase, context.userId);
  });

export const createBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d) => CreateBookingInput.parse(d))
  .handler(async ({ data, context }) => {
    const { createBookingCore } = await import("@/lib/booking.server");
    return createBookingCore(context.userId, data, { confirmOnCreate: true });
  });

export const getMyBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("bookings")
      .select(
        "id, start_at, end_at, status, student_name, phone, notes, member_package_id, lesson_types(name, sport, duration_minutes), member_packages(label, sessions_total, sessions_used)",
      )
      .eq("user_id", userId)
      .order("start_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const cancelBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { transitionBookingStatus } = await import("@/lib/booking.server");
    return transitionBookingStatus({
      bookingId: data.id,
      toStatus: "cancelled",
      supabase: context.supabase,
      ownerUserId: context.userId,
    });
  });
